import type { Material } from "@szyos/contracts";
import { MAX_MATERIAL_FILE_BYTES } from "@szyos/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useId, useRef, useState, type FormEvent } from "react";
import { api, errorMessage, materialFileUrl, type MaterialOwnerRef, type NewMaterial } from "../api";
import { Icon } from "../icons";
import { ErrorNote, useToast } from "./feedback";

/** Fotos werden vor dem Hochladen verkleinert; dabei fallen Metadaten wie der Standort weg. */
const MAX_IMAGE_EDGE = 2560;
const REENCODE = ["image/jpeg", "image/png", "image/webp"];

function toBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk)
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  return btoa(binary);
}

/** Bild im Browser neu kodieren (WebP, höchstens 2560 px). Scheitert das, geht die Originaldatei hoch. */
async function prepareFile(file: File): Promise<{ name: string; buffer: ArrayBuffer }> {
  if (!REENCODE.includes(file.type) || typeof createImageBitmap !== "function") {
    return { name: file.name, buffer: await file.arrayBuffer() };
  }
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_IMAGE_EDGE / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext("2d")?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/webp", 0.86));
    if (!blob) throw new Error("Kodieren fehlgeschlagen");
    return { name: file.name.replace(/\.[^.]*$/, "") + ".webp", buffer: await blob.arrayBuffer() };
  } catch {
    return { name: file.name, buffer: await file.arrayBuffer() };
  }
}

const sizeText = (bytes: number) =>
  bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toLocaleString("de-DE", { maximumFractionDigits: 1 })} MB`
    : `${Math.max(1, Math.round(bytes / 1024)).toLocaleString("de-DE")} KB`;

function MaterialItem({ m, editable, queryKey }: { m: Material; editable: boolean; queryKey: unknown[] }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const refresh = () => queryClient.invalidateQueries({ queryKey });
  // Schalter reagiert sofort; scheitert das Speichern, springt er zurück.
  const [inRepo, setInRepo] = useState(m.inRepo);
  const toggle = useMutation({
    mutationFn: (next: boolean) => api.updateMaterial(m.id, { inRepo: next }),
    onSuccess: async (u) => {
      await refresh();
      toast(u.inRepo ? `„${m.title}“ geht mit ins Repo` : `„${m.title}“ bleibt in szyOS`);
    },
    onError: (err, next) => {
      setInRepo(!next);
      toast(errorMessage(err), "error");
    },
  });
  const remove = useMutation({
    mutationFn: () => api.deleteMaterial(m.id),
    onSuccess: async () => {
      await refresh();
      toast(`„${m.title}“ entfernt`);
    },
    onError: (err) => toast(errorMessage(err), "error"),
  });
  const kindLabel = { note: "Notiz", link: "Link", image: "Bild", file: "Datei" }[m.kind];
  return (
    <li className="material-item">
      {m.kind === "image" && (
        <a href={materialFileUrl(m.id)} target="_blank" rel="noopener noreferrer" className="material-thumb">
          <img src={materialFileUrl(m.id)} alt={m.note || m.title} loading="lazy" />
        </a>
      )}
      <div className="material-text">
        <span className="chip chip--ghost">{kindLabel}</span>{" "}
        {m.kind === "link" && m.url ? (
          <a className="link" href={m.url} target="_blank" rel="noopener noreferrer nofollow">
            {m.title} <span className="sr-only">(neues Fenster)</span>
          </a>
        ) : m.kind === "file" ? (
          <a className="link" href={materialFileUrl(m.id)} download={m.title}>
            {m.title}
          </a>
        ) : (
          <strong>{m.title}</strong>
        )}
        {m.sizeBytes > 0 && <span className="muted mono"> · {sizeText(m.sizeBytes)}</span>}
        {m.note && <p className="material-note">{m.note}</p>}
      </div>
      {editable && (
        <div className="idea-actions">
          <label className="material-repo">
            <input
              type="checkbox"
              checked={inRepo}
              onChange={() => {
                setInRepo(!inRepo);
                toggle.mutate(!inRepo);
              }}
              disabled={toggle.isPending}
            />{" "}
            ins Repo
          </label>
          <button
            type="button"
            className="chip-btn"
            onClick={() => remove.mutate()}
            disabled={remove.isPending}
            aria-label={`Material „${m.title}“ entfernen`}
          >
            <Icon name="trash" /> Entfernen
          </button>
        </div>
      )}
    </li>
  );
}

type Mode = "note" | "link" | "file";

function AddMaterialForm({ owner, queryKey }: { owner: MaterialOwnerRef; queryKey: unknown[] }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [mode, setMode] = useState<Mode>("file");
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [url, setUrl] = useState("");
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const ids = { title: useId(), text: useId(), url: useId(), file: useId() };

  const add = useMutation({
    mutationFn: (input: NewMaterial) => api.addMaterial(owner, input),
    onSuccess: async (m) => {
      setTitle("");
      setText("");
      setUrl("");
      setError(null);
      if (fileRef.current) fileRef.current.value = "";
      await queryClient.invalidateQueries({ queryKey });
      toast(`Material „${m.title}“ hinzugefügt`);
    },
    onError: (err) => setError(errorMessage(err)),
  });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (mode === "note") {
      if (!title.trim() || !text.trim()) return setError("Titel und Text der Notiz angeben.");
      return add.mutate({ kind: "note", title, text });
    }
    if (mode === "link") {
      if (!title.trim() || !/^https?:\/\//.test(url.trim()))
        return setError("Titel und eine Adresse mit https:// angeben.");
      return add.mutate({ kind: "link", title, url: url.trim(), note: text });
    }
    const file = fileRef.current?.files?.[0];
    if (!file) return setError("Datei oder Foto auswählen.");
    const prepared = await prepareFile(file);
    if (prepared.buffer.byteLength > MAX_MATERIAL_FILE_BYTES)
      return setError("Die Datei ist größer als 10 MB.");
    add.mutate({
      kind: "file",
      fileName: prepared.name,
      contentBase64: toBase64(prepared.buffer),
      note: text,
    });
  };

  return (
    <form
      className="form material-add"
      onSubmit={(e) => void submit(e)}
      noValidate
      aria-label="Material hinzufügen"
    >
      <div className="material-modes" role="group" aria-label="Art des Materials">
        {(
          [
            ["file", "Datei oder Foto"],
            ["note", "Notiz"],
            ["link", "Link"],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={`chip-btn${mode === value ? " is-primary" : ""}`}
            aria-pressed={mode === value}
            onClick={() => {
              setMode(value);
              setError(null);
            }}
          >
            {label}
          </button>
        ))}
      </div>
      {mode === "file" && (
        <>
          <label htmlFor={ids.file}>Datei oder Foto (Bilder, PDF, MD, TXT, CSV, JSON, SVG, bis 10 MB)</label>
          <input
            id={ids.file}
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif,application/pdf,.md,.txt,.csv,.json,.svg"
          />
        </>
      )}
      {mode !== "file" && (
        <>
          <label htmlFor={ids.title}>Titel</label>
          <input id={ids.title} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} />
        </>
      )}
      {mode === "link" && (
        <>
          <label htmlFor={ids.url}>Adresse</label>
          <input
            id={ids.url}
            type="url"
            inputMode="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://"
          />
        </>
      )}
      <label htmlFor={ids.text}>{mode === "note" ? "Text" : "Notiz dazu (optional)"}</label>
      <textarea
        id={ids.text}
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={mode === "note" ? 4 : 2}
        maxLength={mode === "note" ? 20000 : 2000}
      />
      <ErrorNote error={error} />
      <button type="submit" className="chip-btn is-primary" disabled={add.isPending}>
        <Icon name="plus" /> {add.isPending ? "Wird hochgeladen …" : "Hinzufügen"}
      </button>
    </form>
  );
}

/**
 * Material an einer Idee oder einem Projekt (UX-06): anzeigen, hinzufügen, entfernen, „ins Repo“ schalten.
 * Bei Projekten mit Repo übernimmt „Ins Repo übernehmen“ das Material per Pull Request nach docs/material.
 */
export function MaterialPanel({
  owner,
  editable,
  repoName,
  heading = "h3",
}: {
  owner: MaterialOwnerRef;
  editable: boolean;
  repoName?: string | null;
  heading?: "h2" | "h3";
}) {
  const toast = useToast();
  const queryKey = ["material", owner.type, owner.id];
  const list = useQuery({ queryKey, queryFn: () => api.material(owner) });
  const [adding, setAdding] = useState(false);
  const [prUrl, setPrUrl] = useState<string | null>(null);
  const headId = useId();
  const toRepo = useMutation({
    mutationFn: () => api.materialToRepo(owner.id),
    onSuccess: (r) => {
      setPrUrl(r.prUrl);
      toast(`Pull Request mit ${r.files} ${r.files === 1 ? "Datei" : "Dateien"} geöffnet`);
    },
    onError: (err) => toast(errorMessage(err), "error"),
  });
  const items = list.data ?? [];
  const Heading = heading;
  return (
    <section className="material" aria-labelledby={headId}>
      <Heading id={headId} className="material-head">
        <Icon name="file" /> Material ({items.length})
      </Heading>
      {list.isError && <ErrorNote error={errorMessage(list.error)} />}
      {items.length > 0 && (
        <ul className="material-list">
          {items.map((m) => (
            <MaterialItem key={m.id} m={m} editable={editable} queryKey={queryKey} />
          ))}
        </ul>
      )}
      {editable && (
        <div className="idea-actions">
          <button
            type="button"
            className="chip-btn"
            onClick={() => setAdding((a) => !a)}
            aria-expanded={adding}
          >
            <Icon name="plus" /> Material hinzufügen
          </button>
          {owner.type === "projects" && repoName && items.some((m) => m.inRepo) && (
            <button
              type="button"
              className="chip-btn"
              onClick={() => toRepo.mutate()}
              disabled={toRepo.isPending}
            >
              <Icon name="pr" /> Ins Repo übernehmen
            </button>
          )}
        </div>
      )}
      {prUrl && (
        <p className="material-pr" role="status">
          Pull Request für {repoName}:{" "}
          <a className="link" href={prUrl} target="_blank" rel="noopener noreferrer">
            ansehen und mergen
          </a>
        </p>
      )}
      {editable && adding && <AddMaterialForm owner={owner} queryKey={queryKey} />}
    </section>
  );
}
